import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from "@rosc/ui/v32/menu";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ChartPie,
  ChevronsUpDown,
  LayoutTemplate,
  LogOut,
  MessageSquareText,
  Monitor,
  Moon,
  Settings,
  Sparkles,
  Sun,
} from "lucide-react";
import type { JSX } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { shallow } from "zustand/shallow";
import { AppConfig } from "@/common/appConfig";
import { SignOutRecordingsDialog } from "@/components/ui/offline-recording/SignOutRecordingsDialog";
import { V3ConfirmSheet } from "@/components/ui/v3/confirm-sheet";
import { planDisplayName } from "@/lib/plan-name";
import { useOwnUsage } from "@/lib/usage-queries";
import { cn } from "@/lib/utils";
import { ColorTheme, useAppSettingsStore } from "@/store/appSettings.store";
import { useAuthStore } from "@/store/auth.store";
import { useFeedbackStore } from "@/store/feedback.store";
import { useOfflineRecordingStore } from "@/store/offlineRecording.store";
import { useWhatsNewStore } from "@/store/whatsNew.store";

// "DC" from the account's name, else the first letter of the email.
export const accountInitials = (
  first?: string | null,
  last?: string | null,
  email?: string | null,
): string => {
  const letters = [first, last]
    .map((part) => part?.trim().charAt(0) ?? "")
    .join("");
  if (letters) return letters.toLocaleUpperCase();
  return (email?.trim().charAt(0) ?? "?").toLocaleUpperCase();
};

// The account avatar (ink circle, as on iPad) and its menu: Templates and
// Analytics, Settings, What's new, Send feedback, Dark mode and Sign out —
// the items the classic sidebar listed on its own.
// "Pro · 412 min left": the plan and this month's recording minutes when
// the (already cached) usage read has them, else the email.
export const accountSubline = (
  t: (key: string, options?: Record<string, unknown>) => string,
  {
    planName,
    email,
    minutesLeft,
  }: {
    planName?: string | null;
    email?: string | null;
    minutesLeft?: number | null;
  },
): string => {
  const parts = [
    planDisplayName(planName),
    minutesLeft === undefined || minutesLeft === null
      ? null
      : t("v3.shell.minutesLeft", { count: minutesLeft }),
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : (email ?? "");
};

const THEME_OPTIONS = [
  { value: ColorTheme.Light, Icon: Sun },
  { value: ColorTheme.Dark, Icon: Moon },
  { value: ColorTheme.System, Icon: Monitor },
] as const;

export const AccountMenu = ({
  align = "start",
  className,
  variant = "avatar",
}: {
  align?: "start" | "end";
  className?: string;
  // "avatar": the round button (phone headers). "row": the account row at
  // the bottom of the desktop sidebar, opening upward. "rail": the avatar at
  // the bottom of the hidden sidebar's rail.
  variant?: "avatar" | "row" | "rail";
}): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { visitPublicId?: string };
  const userData = useAuthStore((state) => state.userData);
  const { theme, setTheme } = useAppSettingsStore(
    (state) => ({ theme: state.theme, setTheme: state.setTheme }),
    shallow,
  );
  const unsent = useOfflineRecordingStore((state) => state.recordings.length);
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [guard, setGuard] = useState(false);
  const [busy, setBusy] = useState(false);
  const name = [userData?.firstname, userData?.lastname]
    .filter(Boolean)
    .join(" ");
  const initials = accountInitials(
    userData?.firstname,
    userData?.lastname,
    userData?.email,
  );

  const usage = useOwnUsage(variant === "row");
  const minutesLeft = usage.data
    ? Math.floor(usage.data.subscription.availableSeconds / 60) +
      Math.floor(usage.data.additional.availableSeconds / 60)
    : null;
  const subline = accountSubline(t as never, {
    planName: userData?.planName,
    email: userData?.email,
    minutesLeft,
  });
  const avatar = (
    <span
      aria-hidden
      className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-v3-ink text-[11.5px] font-semibold tracking-wide text-v3-on-ink lg:size-7 lg:text-[11px]"
    >
      {initials}
    </span>
  );

  const signOut = async (): Promise<void> => {
    await useAuthStore.getState().signOut();
    await navigate({ to: "/" });
  };

  return (
    <>
      <Menu>
        <MenuTrigger asChild>
          {variant === "row" ? (
            <button
              aria-label={t("v3.shell.accountOf", { name: name || subline })}
              className={cn(
                "group flex min-h-12 w-full items-center gap-2.5 rounded-[10px] px-2 py-1.5 text-left font-v3 transition-colors hover:bg-[rgba(127,127,140,0.08)] focus-visible:outline-2 focus-visible:outline-v3-brand data-[state=open]:bg-v3-panel data-[state=open]:shadow-v3-hairline",
                className,
              )}
              data-testid="account-menu"
              type="button"
            >
              {avatar}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13.5px] font-semibold text-v3-text">
                  {name || userData?.email || t("v3.shell.account")}
                </span>
                {subline && (
                  <span
                    className="block truncate text-[12px] tabular-nums text-v3-text-3"
                    data-testid="account-subline"
                  >
                    {subline}
                  </span>
                )}
              </span>
              <ChevronsUpDown
                aria-hidden
                className="size-4 shrink-0 text-v3-text-3 group-hover:text-v3-text-2"
              />
            </button>
          ) : (
            <button
              aria-label={t("v3.shell.account")}
              className={cn(
                "inline-flex size-11 shrink-0 items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v3-brand lg:size-9",
                className,
              )}
              data-testid="account-menu"
              title={variant === "rail" ? name || undefined : undefined}
              type="button"
            >
              {avatar}
            </button>
          )}
        </MenuTrigger>
        <MenuContent
          align={
            variant === "row" ? "start" : variant === "rail" ? "end" : align
          }
          className={cn(
            variant === "row"
              ? "w-[var(--radix-dropdown-menu-trigger-width)] min-w-60"
              : "w-64",
          )}
          side={
            variant === "avatar"
              ? "bottom"
              : variant === "rail"
                ? "right"
                : "top"
          }
          sideOffset={6}
        >
          {(name || userData?.email) && (
            <div className="px-3 pb-2 pt-2">
              {name && (
                <p className="truncate text-[14px] font-semibold">{name}</p>
              )}
              {userData?.email && (
                <p className="truncate text-[12.5px] text-v3-text-3">
                  {userData.email}
                </p>
              )}
            </div>
          )}
          <MenuSeparator />
          <MenuItem onSelect={() => void navigate({ to: "/templates" })}>
            <LayoutTemplate aria-hidden />
            {t("templates.title")}
          </MenuItem>
          <MenuItem onSelect={() => void navigate({ to: "/analytics" })}>
            <ChartPie aria-hidden />
            {t("dashboard.title")}
          </MenuItem>
          <MenuItem
            data-testid="account-settings"
            onSelect={() => void navigate({ to: "/settings" })}
          >
            <Settings aria-hidden />
            {t("settings.title")}
          </MenuItem>
          <MenuSeparator />
          {AppConfig.enableWhatsNew && (
            <MenuItem onSelect={() => useWhatsNewStore.getState().show()}>
              <Sparkles aria-hidden />
              {t("sidebar.whatsNew")}
            </MenuItem>
          )}
          <MenuItem
            data-testid="send-feedback-menu"
            onSelect={() =>
              useFeedbackStore
                .getState()
                .show({ visitPublicId: params.visitPublicId ?? null })
            }
          >
            <MessageSquareText aria-hidden />
            {t("sidebar.sendFeedback")}
          </MenuItem>
          {/* Appearance: Light, Dark or System (follows the device), as
              menu radio items so arrow keys reach them. The menu stays open,
              so the change is seen in place. */}
          <MenuSeparator />
          <MenuLabel>{t("v3.shell.theme.label")}</MenuLabel>
          <MenuRadioGroup
            data-testid="account-theme"
            value={theme}
            onValueChange={(value) => setTheme(value as ColorTheme)}
          >
            {THEME_OPTIONS.map(({ value, Icon }) => (
              <MenuRadioItem
                key={value}
                data-testid={`account-theme-${value}`}
                value={value}
                onSelect={(event) => event.preventDefault()}
              >
                <Icon aria-hidden />
                {t(`v3.shell.theme.${value}`)}
              </MenuRadioItem>
            ))}
          </MenuRadioGroup>
          <MenuSeparator />
          <MenuItem
            data-testid="account-sign-out"
            onSelect={() => {
              // Offline recording: never sign out silently over unsent
              // recordings.
              if (unsent > 0) setGuard(true);
              else setConfirmSignOut(true);
            }}
          >
            <LogOut aria-hidden />
            {t("v3.shell.signOut")}
          </MenuItem>
        </MenuContent>
      </Menu>
      <V3ConfirmSheet
        confirmLabel={t("v3.shell.signOut")}
        description={t("v3.shell.signOutDescription")}
        open={confirmSignOut}
        testId="sign-out-confirm"
        title={t("v3.shell.signOutTitle")}
        onConfirm={() => void signOut()}
        onOpenChange={setConfirmSignOut}
      />
      <SignOutRecordingsDialog
        busy={busy}
        count={unsent}
        open={guard}
        onCancel={() => setGuard(false)}
        onDelete={() => {
          setBusy(true);
          void useOfflineRecordingStore
            .getState()
            .discardAll()
            .catch(() => undefined)
            .then(signOut)
            .finally(() => {
              setBusy(false);
              setGuard(false);
            });
        }}
        onKeep={() => {
          setGuard(false);
          void signOut();
        }}
      />
    </>
  );
};
